// Library methods
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import SharedModal from "src/components/shared/modals/SharedModal/SharedModal";
import TableCard from "src/components/shared/tables/TableCard/TableCard";
import ViewEMRModal from "./ViewEMRModal/ViewEMRModal";
import { DownloadIcon, TrashIcon, ViewFileIcon } from "src/assets/icons";

// Types
import { ExtendedUnassignedEMRType } from "src/services/emr/types";

// Styles
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

// Hooks
import { useDeleteUnassignedEMR } from "src/services/emr/hooks/useDeleteUnassignedEMR";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

type ImportedEMRCardType = {
  exam: ExtendedUnassignedEMRType;
};

const ImportedEMRCard = ({ exam }: ImportedEMRCardType) => {
  const { t } = useTranslation();
  const { clinicInfo } = useClinicContext();

  const theme = useTheme();
  const smallerThanSm = useMediaQuery(theme.breakpoints.down("sm"));

  const { mutate: deleteMutate, isPending: deletePending } =
    useDeleteUnassignedEMR(clinicInfo?.id);

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openViewModal, setOpenViewModal] = useState(false);

  const patientId = useMemo(() => exam?.patientId, [exam?.patientId]);
  // the extracted patient, not the matched one — the match is a suggestion and
  // is only ever shown alongside the extracted data in the view modal
  const patientName = useMemo(
    () => exam?.patientName ?? "N/A",
    [exam?.patientName]
  );
  const patientBirthdate = useMemo(
    () => exam?.patientBirthdate ?? "N/A",
    [exam?.patientBirthdate]
  );

  const importDate = useMemo(
    () => (exam?.createdAt ? exam?.createdAt?.slice(0, 10) : "N/A"),
    [exam?.createdAt]
  );

  const examType = useMemo(() => t(exam?.examType), [t, exam?.examType]);

  const examStatus = useMemo(() => t(exam?.status), [t, exam?.status]);

  // buttons are ordered in terms of importance / usage
  const examMenuButtons = useMemo(() => {
    const allButtons = [
      {
        icon: <DownloadIcon />,
        text: t("Download Imported Exam"),
        tooltipText: t("Download Imported Exam"),
        onClick: () => {
          window.open(exam.fileUrl, "_blank");
        },
        iconColor: "#FFF",
        display: Boolean(exam.fileUrl),
        disabled: deletePending,
        href: undefined,
      },
      {
        icon: <ViewFileIcon />,
        text: t("View Imported Exam"),
        tooltipText: t("View Imported Exam"),
        onClick: () => setOpenViewModal(true),
        iconColor: "#FFF",
        display: exam?.status === "ConfirmPending",
        disabled: deletePending,
        href: undefined,
      },
      {
        icon: <TrashIcon />,
        text: t("Delete Imported Exam"),
        tooltipText: t("Delete Imported Exam"),
        onClick: () => setOpenDeleteModal(true),
        iconColor: "#FFF",
        display: true,
        disabled: deletePending,
        href: undefined,
      },
    ];

    return allButtons?.filter((examButton) => examButton?.display);
  }, [t, exam.fileUrl, exam.status, deletePending]);

  const handleDelete = useCallback(() => {
    deleteMutate(exam?.jobId, { onSuccess: () => setOpenDeleteModal(false) });
  }, [exam?.jobId, deleteMutate]);

  return (
    <Grid container item xs={12}>
      <Grid item xs={12}>
        <TableCard
          variant="dark"
          hasHoverState={false}
          isSelectedForDeletion={false}
          isSelectedForViewing={false}
          onClick={() => {}}
          backgroundColorOverride={
            theme.palette.mode === "dark" ? "#00000090" : undefined
          }
        >
          <Grid container item xs={12} alignItems="center">
            <Grid container item xs={10}>
              {/* Patient name */}
              <Grid item xs={12} sm={2.4}>
                <Tooltip title={patientName}>
                  <Typography
                    sx={{
                      maxWidth: "fit-content",
                      display: "block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      fontSize: "14px",
                      paddingRight: "16px",
                    }}
                  >
                    {smallerThanSm && (
                      <span style={{ fontWeight: 600 }}>{t("Patient")}: </span>
                    )}
                    {patientName}
                  </Typography>
                </Tooltip>
              </Grid>

              {/* Patient birthdate */}
              <Grid item xs={12} sm={2.4}>
                <Tooltip title={patientBirthdate}>
                  <Typography
                    sx={{
                      maxWidth: "fit-content",
                      display: "block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      fontSize: "14px",
                      paddingRight: "16px",
                    }}
                  >
                    {smallerThanSm && (
                      <span style={{ fontWeight: 600 }}>
                        {t("Birthdate")}:{" "}
                      </span>
                    )}
                    {patientBirthdate}
                  </Typography>
                </Tooltip>
              </Grid>

              {/* Exam type */}
              <Grid item xs={12} sm={2.4}>
                <Tooltip title={examType}>
                  <Typography
                    sx={{
                      maxWidth: "fit-content",
                      display: "block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      fontSize: "14px",
                      paddingRight: "16px",
                    }}
                  >
                    {smallerThanSm && (
                      <span style={{ fontWeight: 600 }}>{t("Type")}: </span>
                    )}
                    {examType}
                  </Typography>
                </Tooltip>
              </Grid>

              {/* Exam status */}
              <Grid item xs={12} sm={2.4}>
                <Tooltip title={examStatus}>
                  <Typography
                    sx={{
                      maxWidth: "fit-content",
                      display: "block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      fontSize: "14px",
                      paddingRight: "16px",
                    }}
                  >
                    {smallerThanSm && (
                      <span style={{ fontWeight: 600 }}>{t("Status")}: </span>
                    )}
                    {examStatus}
                  </Typography>
                </Tooltip>
              </Grid>

              {/* Import date */}
              <Grid item xs={12} sm={2.4}>
                <Tooltip title={importDate}>
                  <Typography
                    sx={{
                      maxWidth: "fit-content",
                      display: "block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      fontSize: "14px",
                      paddingRight: "16px",
                    }}
                  >
                    {smallerThanSm && (
                      <span style={{ fontWeight: 600 }}>
                        {t("Import Date")}:{" "}
                      </span>
                    )}
                    {importDate}
                  </Typography>
                </Tooltip>
              </Grid>
            </Grid>

            {/* Action icons */}
            <Grid
              item
              xs={2}
              sx={{
                display: "flex",
                height: "100%",
                justifyContent: "flex-end",
                alignItems: "center",
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              {examMenuButtons.map((examButton) => (
                <Tooltip
                  key={examButton.tooltipText}
                  title={examButton.tooltipText}
                >
                  <span>
                    <a
                      href={examButton.href}
                      style={{
                        textDecoration: "none",
                        color: examButton.iconColor,
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        examButton.onClick();
                      }}
                    >
                      <IconButton
                        disableRipple
                        disableFocusRipple
                        disableTouchRipple
                        sx={{
                          py: { xs: "8px", sm: "0px" },
                          color: examButton.iconColor,
                          "&.Mui-disabled": {
                            color: examButton.iconColor,
                            opacity: "0.5",
                          },
                          display: !examButton.display ? "none" : "block",
                        }}
                        disabled={!examButton.display}
                      >
                        {examButton.icon}
                      </IconButton>
                    </a>
                  </span>
                </Tooltip>
              ))}
            </Grid>
          </Grid>
        </TableCard>
      </Grid>

      {openViewModal && (
        <ViewEMRModal
          patientId={patientId ?? ""}
          patientName={patientName}
          open={openViewModal}
          exam={exam}
          onClose={() => setOpenViewModal(false)}
        />
      )}

      {/* Delete Exam Modal */}
      <SharedModal
        title={t("Delete Exam?")}
        modalDescription={t(
          "Are you sure you want to delete the selected exam?"
        )}
        confirmButtonText={t("Delete")}
        confirmButtonVariant="error"
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        onConfirm={handleDelete}
      />
    </Grid>
  );
};

export default ImportedEMRCard;
